import { WorkspaceInvitationComponent } from './../components/workspace-invitation/workspace-invitation.component';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
  catchError,
  first,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  timeout,
} from 'rxjs';
import { ApiService } from '../api.service';
import { AuthService } from '../auth.service';
import { LoginCallback } from '../models/login-callback';
import { LoginCallbackRequest } from '../models/login-callback-request';
import { Organization } from '../models/organization';
import { UserOrganization } from '../models/user-organization';
import * as uuid from 'uuid';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { CreateOrganisationModalComponent } from '../modals/create-organisation-modal/create-organisation-modal.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TermsCondtionsComponent } from '../components/terms-condtions/terms-condtions.component';
import { IPendingUserInvitationResponse } from '../models/pending-user-invitation.response';
import { DynamicFormDialogComponent } from '../dynamic-forms/dynamic-form-dialog.component';
import { DisabledWorkspaceDialogComponent } from '../modals/disabled-workspace-dialog/disabled-workspace-dialog.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit {
  next: string = '/';
  loadingComplete: boolean = false;
  requestingApp: string | undefined;
  failureRedirect: string = '';
  autoLogin: boolean = false;
  public organizationUser: UserOrganization | null = null;
  public allUserOrganizations: Organization[] = [];

  public personalOrganizations: Organization[] = [];
  public businessOrganizations: Organization[] = [];
  public searchedOrganizations: Organization[] = [];
  public cardsDisabled: boolean = false;
  public creatingOrg: boolean = false;
  searchValue: string = '';
  pendingInvitations: IPendingUserInvitationResponse[] = [];
  inviteDetails: {
    publicId: string;
    email: string;
  } | null = null;

  constructor(
    private api: ApiService,
    private authService: AuthService,
    private router: Router,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.queryParams.pipe(first()).subscribe((params) => {
      this.next = params['next'] ?? this.next;
      const automaticOrganisationSignIn = params['orgId'];
      this.failureRedirect = params['redirect_uri'] ?? window.location.href;
      // TODO: should use the client display name from iam. dont pass it like this
      this.requestingApp = params['requesting_application'] ?? undefined;
      this.autoLogin = (params['autoLogin'] ?? 'false') === 'true';
      if (params['invitePublicId'] && params['inviteEmail']) {
        this.inviteDetails = {
          publicId: params['invitePublicId'],
          email: params['inviteEmail'],
        };
      }
      this.fetchUserData(
        automaticOrganisationSignIn ? Number(automaticOrganisationSignIn) : null
      );
    });
  }

  fetchUserData(autoSignInOrg: number | null) {
    const authRequest: LoginCallbackRequest = { org_id: autoSignInOrg };

    this.api
      .getAuthToken(authRequest)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          return of(null);
        }),
        switchMap((data: LoginCallback | null) => {
          if (data) {
            this.authService.setToken(data.token, data.org_id, data.scopes);

            return forkJoin([
              this.getOrganizations(),
              this.getOrganizationUser(),
            ]);
          }
          return of(null);
        }),
        first()
      )
      .subscribe((data: [null, UserOrganization | null] | null) => {
        if (data![1] && data![1].user) {
          let user = data![1].user;
          if (user.accepted_terms_at) {
            this.orgSelection(autoSignInOrg);
            return;
          }
          this.openTermsAndConditions(autoSignInOrg);
        }
      });
  }

  orgSelection(autoSignInOrg: number | null) {
    if (autoSignInOrg) {
      this.signInOrganization(autoSignInOrg);
    } else if (this.autoLogin && this.businessOrganizations.length === 1) {
      this.signInOrganization(this.businessOrganizations[0].id);
    } else {
      this.loadingComplete = true;
    }
  }

  signInOrganization(nextOrgId: number, revoked: boolean = false) {
    if (!this.cardsDisabled && !revoked) {
      const authRequest: LoginCallbackRequest = {
        org_id: nextOrgId ? Number(nextOrgId) : null,
      };
      this.api.getAuthToken(authRequest).subscribe((data: LoginCallback) => {
        this.authService.logout();
        this.authService.setToken(data.token, data.org_id, data.scopes);

        if (this.authService.isAuthenticated() && nextOrgId != null) {
          this.signIn();
          return;
        }
      });
    }
  }

  searchWorkspaces() {
    this.searchedOrganizations = [];
    if (this.searchValue) {
      this.searchedOrganizations = this.businessOrganizations.filter(
        (item) =>
          item.name
            .toLowerCase()
            .trim()
            .indexOf(this.searchValue.toLowerCase().trim()) > -1
      );
    }
  }

  newOrganizationCreated(org: Organization) {
    this.signInOrganization(org.id);
  }

  openAddOrgDialog() {
    this.dialog
      .open(CreateOrganisationModalComponent, { width: '400px' })
      .afterClosed()
      .subscribe((businessName) => {
        if (businessName) this.CreateOrganisation(businessName);
      });
  }

  getOrganizations() {
    return this.api.getUserOrganizations(0).pipe(
      catchError((err: HttpErrorResponse) => {
        console.error(err);
        return of(null);
      }),
      map((data: Organization[] | null) => {
        if (data) {
          this.allUserOrganizations = data;

          this.personalOrganizations = data.filter((x) => x.is_sole_proprietor);
          this.businessOrganizations = data.filter(
            (x) => x.is_sole_proprietor == false
          );
        }

        return null;
      })
    );
  }

  openDisabledDialog() {
    const dialogRef = this.dialog.open(DisabledWorkspaceDialogComponent, {
      width: '650px',
    });
  }

  getOrganizationUser() {
    return this.api.getOrganizationUser(0, 0).pipe(
      catchError((err) => {
        console.error(err);
        return of(null);
      }),
      map((data: UserOrganization | null) => {
        if (data) {
          this.getPendingInvitations(data.user.id);
          this.organizationUser = data;
        } else this.logout();
        return data;
      })
    );
  }

  getPendingInvitations(clientId: number | string) {
    this.pendingInvitations = [];
    this.api.getPendingInvitations(clientId).subscribe((val) => {
      this.pendingInvitations = val;
      if (this.inviteDetails) {
        let invite = this.pendingInvitations.find(
          (x) => x.public_id == this.inviteDetails?.publicId
        );
        if (invite) {
          this.viewPendingInvitation(invite);
        } else {
          this.snack.open(
            'The invitation you are trying to view is not found.',
            undefined,
            { duration: 3000, panelClass: 'errorSnackbar' }
          );
        }
        this.inviteDetails = null;
      }
    });
  }

  viewPendingInvitation(item: IPendingUserInvitationResponse) {
    if (item) {
      this.dialog
        .open(WorkspaceInvitationComponent, {
          disableClose: true,
          width: '450px',
          data: item,
        })
        .beforeClosed()
        .subscribe((val) => {
          if (val) {
            let id = this.organizationUser?.user.id;
            if (id) {
              this.getPendingInvitations(id);
            }
            if (val == 'accepted') {
              this.getOrganizations().pipe(first()).subscribe();
            }
            if (this.inviteDetails) {
              this.inviteDetails = null;
            }
          }
        });
    }
  }

  reloadPage() {
    this.router.navigateByUrl(this.router.url, { skipLocationChange: false });
  }

  logout() {
    // TODO: improve cache busting
    const cb = Math.floor(Math.random() * 10000).toString();
    this.router.navigate(['/logout'], {
      queryParams: {
        redirect_url:
          window.location.href +
          (window.location.href.indexOf('?') == -1 ? `?b=${cb}` : `&b=${cb}`),
      },
    });
  }

  signIn() {
    if (this.authService.isAuthenticated()) {
      const next =
        this.next.startsWith('/authorize') && !this.next.includes('orgId')
          ? this.next + `&orgId=${this.authService.getOrgId()}`
          : this.next;
      this.router.navigateByUrl(next);
    }
  }

  CreateOrganisation(businessName: string) {
    this.creatingOrg = true;
    this.api
      .createOrganization({
        display_name: uuid.v4(),
        name: businessName,
      })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.error?.message) {
            this.snack.open(err.error.message, undefined, {
              duration: 3000,
              panelClass: 'errorSnackbar',
            });
          } else if (err.error?.error) {
            this.snack.open(err.error.error, undefined, {
              duration: 3000,
              panelClass: 'errorSnackbar',
            });
          } else if (err.error) {
            this.snack.open(err.error, undefined, {
              duration: 3000,
              panelClass: 'errorSnackbar',
            });
          } else {
            this.snack.open(
              'An error occurred adding your workspace',
              'dismiss',
              { duration: 3000, panelClass: 'errorSnackbar' }
            );
          }
          return of(null);
        })
      )
      .subscribe((result: any) => {
        if (result) {
          this.allUserOrganizations.push(result);
          if (result.is_sole_proprietor)
            this.personalOrganizations.push(result);
          else this.businessOrganizations.push(result);
        }
        this.disableCards(false);
        this.creatingOrg = false;
      });
  }

  disableCards(disable: boolean) {
    this.cardsDisabled = disable;
  }

  openTermsAndConditions(autoSignInOrg: number | null) {
    this.dialog
      .open(TermsCondtionsComponent, { disableClose: true, width: '40rem' })
      .beforeClosed()
      .subscribe(() => {
        this.orgSelection(autoSignInOrg);
      });
  }
}
